// apps/admin/src/router/index.tsx
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { PageSkeleton } from "@/components/ui/PageSkeleton";
import { RoleRoute } from "@/components/RoleRoute";

// Layout
const AdminLayout = lazy(() => import("@/components/layout/AdminLayout"));

// Auth & Error pages
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const AccessDeniedPage = lazy(() => import("@/pages/AccessDeniedPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const ServerErrorPage = lazy(() => import("@/pages/ServerErrorPage"));
const MaintenancePage = lazy(() => import("@/pages/MaintenancePage"));

// Core pages
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const OrdersPage = lazy(() => import("@/pages/OrdersPage"));
const AdminNewOrderPage = lazy(() => import("@/pages/AdminNewOrderPage"));
const ShipmentsPage = lazy(() => import("@/pages/ShipmentsPage"));
const LRPage = lazy(() => import("@/pages/LRPage"));
const ClientsPage = lazy(() => import("@/pages/ClientsPage"));
const VendorsPage = lazy(() => import("@/pages/VendorsPage"));
const DriversPage = lazy(() => import("@/pages/DriversPage"));
const VehiclesPage = lazy(() => import("@/pages/VehiclesPage"));
const KycQueuePage = lazy(() => import("@/pages/KycQueuePage"));

// Day 36
const RoutesPage = lazy(() => import("@/pages/RoutesPage"));
const UsersPage = lazy(() => import("@/pages/UsersPage"));

// Day 37
const AuditLogsPage = lazy(() => import("@/pages/AuditLogsPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));

// Day 41
const FinancePage = lazy(() => import("@/pages/FinancePage"));
const InvoicesPage = lazy(() => import("@/pages/InvoicesPage"));
const RevenueReportPage = lazy(() => import("@/pages/RevenueReportPage"));

// Day 42
const PaymentsPage = lazy(() => import("@/pages/PaymentsPage"));
const OutstandingDuesPage = lazy(() => import("@/pages/OutstandingDuesPage"));
const CodReconciliationPage = lazy(
  () => import("@/pages/CodReconciliationPage"),
);

// Day 45
const FleetMapPage = lazy(() => import("@/pages/FleetMapPage"));

// Day 47-48
const AgentsPage = lazy(() => import("@/pages/AgentsPage"));
const AgentDetailPage = lazy(() => import("@/pages/AgentDetailPage"));

// Day 50
const VendorPerformancePage = lazy(
  () => import("@/pages/VendorPerformancePage"),
);
const ClientVolumePage = lazy(() => import("@/pages/ClientVolumePage"));
const RouteAnalyticsPage = lazy(() => import("@/pages/RouteAnalyticsPage"));

const S = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageSkeleton />}>{children}</Suspense>
);

// Day 75
const PoliceFinesPage = lazy(() => import("@/pages/PoliceFinesPage"));
// Vehicle Log
const VehicleLogPage = lazy(() => import("@/pages/VehicleLogPage"));

const VehicleHireContractsPage = lazy(
  () => import("@/pages/VehicleHireContractsPage"),
);

const StoresPage = lazy(() => import("@/pages/StoresPage"));
const PickupAddressesPage = lazy(() => import("@/pages/PickupAddressesPage"));
const LoadingMemosPage = lazy(() => import("@/pages/LoadingMemosPage"));
// Driver Devices (super_admin only)
const DriverDevicesPage = lazy(() => import("@/pages/DriverDevicesPage"));
const AcceptInvitePage = lazy(() => import("@/pages/AcceptInvitePage"));

export function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public */}
        <Route
          path="/login"
          element={
            <S>
              <LoginPage />
            </S>
          }
        />
        <Route
          path="/403"
          element={
            <S>
              <AccessDeniedPage />
            </S>
          }
        />
        <Route
          path="/accept-invite"
          element={
            <S>
              <AcceptInvitePage />
            </S>
          }
        />

        {/* Protected admin routes */}
        <Route
          element={
            <S>
              <AdminLayout />
            </S>
          }
        >
          <Route
            path="/dashboard"
            element={
              <S>
                <DashboardPage />
              </S>
            }
          />
          <Route
            path="/orders"
            element={
              <S>
                <OrdersPage />
              </S>
            }
          />
          <Route
            path="/orders/new"
            element={
              <S>
                <AdminNewOrderPage />
              </S>
            }
          />
          <Route
            path="/shipments"
            element={
              <S>
                <ShipmentsPage />
              </S>
            }
          />
          <Route
            path="/lr"
            element={
              <S>
                <RoleRoute
                  allowedRoles={["super_admin", "admin", "area_manager"]}
                >
                  <LRPage />
                </RoleRoute>
              </S>
            }
          />
          <Route
            path="/clients"
            element={
              <S>
                <ClientsPage />
              </S>
            }
          />
          <Route
            path="/vendors"
            element={
              <S>
                <RoleRoute
                  allowedRoles={[
                    "super_admin",
                    "admin",
                    "ground_team",
                    "area_manager",
                  ]}
                >
                  <VendorsPage />
                </RoleRoute>
              </S>
            }
          />
          <Route
            path="/drivers"
            element={
              <S>
                <RoleRoute
                  allowedRoles={[
                    "super_admin",
                    "admin",
                    "ground_team",
                    "area_manager",
                  ]}
                >
                  <DriversPage />
                </RoleRoute>
              </S>
            }
          />
          <Route
            path="/vehicles"
            element={
              <S>
                <RoleRoute
                  allowedRoles={[
                    "super_admin",
                    "admin",
                    "ground_team",
                    "area_manager",
                  ]}
                >
                  <VehiclesPage />
                </RoleRoute>
              </S>
            }
          />
          <Route
            path="/kyc-queue"
            element={
              <S>
                <RoleRoute
                  allowedRoles={["super_admin", "admin", "area_manager"]}
                >
                  <KycQueuePage />
                </RoleRoute>
              </S>
            }
          />

          {/* Day 36 */}
          <Route
            path="/routes"
            element={
              <S>
                <RoleRoute
                  allowedRoles={[
                    "super_admin",
                    "admin",
                    "ground_team",
                    "area_manager",
                  ]}
                >
                  <RoutesPage />
                </RoleRoute>
              </S>
            }
          />
          <Route
            path="/users"
            element={
              <S>
                <RoleRoute allowedRoles={["super_admin", "admin"]}>
                  <UsersPage />
                </RoleRoute>
              </S>
            }
          />

          {/* Day 37 */}
          <Route
            path="/audit-logs"
            element={
              <S>
                <RoleRoute allowedRoles={["super_admin"]}>
                  <AuditLogsPage />
                </RoleRoute>
              </S>
            }
          />
          <Route
            path="/settings"
            element={
              <S>
                <RoleRoute allowedRoles={["super_admin"]}>
                  <SettingsPage />
                </RoleRoute>
              </S>
            }
          />

          {/* Day 41 */}
          <Route
            path="/finance"
            element={
              <S>
                <RoleRoute allowedRoles={["super_admin", "admin"]}>
                  <FinancePage />
                </RoleRoute>
              </S>
            }
          />
          <Route
            path="/invoices"
            element={
              <S>
                <RoleRoute allowedRoles={["super_admin", "admin"]}>
                  <InvoicesPage />
                </RoleRoute>
              </S>
            }
          />
          <Route
            path="/reports"
            element={
              <S>
                <RoleRoute allowedRoles={["super_admin"]}>
                  <RevenueReportPage />
                </RoleRoute>
              </S>
            }
          />

          {/* Day 50 */}
          <Route
            path="/reports/vendor-performance"
            element={
              <S>
                <RoleRoute allowedRoles={["super_admin", "admin"]}>
                  <VendorPerformancePage />
                </RoleRoute>
              </S>
            }
          />
          <Route
            path="/reports/client-volume"
            element={
              <S>
                <RoleRoute allowedRoles={["super_admin", "admin"]}>
                  <ClientVolumePage />
                </RoleRoute>
              </S>
            }
          />
          <Route
            path="/reports/route-analytics"
            element={
              <S>
                <RoleRoute allowedRoles={["super_admin", "admin"]}>
                  <RouteAnalyticsPage />
                </RoleRoute>
              </S>
            }
          />

          {/* Day 42 */}
          <Route
            path="/payments"
            element={
              <S>
                <RoleRoute allowedRoles={["super_admin"]}>
                  <PaymentsPage />
                </RoleRoute>
              </S>
            }
          />
          <Route
            path="/outstanding-dues"
            element={
              <S>
                <RoleRoute allowedRoles={["super_admin"]}>
                  <OutstandingDuesPage />
                </RoleRoute>
              </S>
            }
          />
          <Route
            path="/cod-reconciliation"
            element={
              <S>
                <RoleRoute allowedRoles={["super_admin"]}>
                  <CodReconciliationPage />
                </RoleRoute>
              </S>
            }
          />

          {/* Day 45 */}
          <Route
            path="/fleet-map"
            element={
              <S>
                <RoleRoute
                  allowedRoles={[
                    "super_admin",
                    "admin",
                    "ground_team",
                    "area_manager",
                  ]}
                >
                  <FleetMapPage />
                </RoleRoute>
              </S>
            }
          />
          {/* Day 75 */}
          <Route
            path="/police-fines"
            element={
              <S>
                <RoleRoute
                  allowedRoles={["super_admin", "admin", "area_manager"]}
                >
                  <PoliceFinesPage />
                </RoleRoute>
              </S>
            }
          />

          <Route
            path="/vehicle-log"
            element={
              <S>
                <RoleRoute
                  allowedRoles={["super_admin", "admin", "area_manager"]}
                >
                  <VehicleLogPage />
                </RoleRoute>
              </S>
            }
          />

          <Route
            path="/vehicle-hire-contracts"
            element={
              <S>
                <RoleRoute allowedRoles={["super_admin"]}>
                  <VehicleHireContractsPage />
                </RoleRoute>
              </S>
            }
          />

          <Route
            path="/stores"
            element={
              <S>
                <RoleRoute allowedRoles={["super_admin", "admin"]}>
                  <StoresPage />
                </RoleRoute>
              </S>
            }
          />

          <Route
            path="/pickup-addresses"
            element={
              <S>
                <RoleRoute allowedRoles={["super_admin"]}>
                  <PickupAddressesPage />
                </RoleRoute>
              </S>
            }
          />

          <Route
            path="/loading-memos"
            element={
              <S>
                <RoleRoute
                  allowedRoles={["super_admin", "admin", "area_manager"]}
                >
                  <LoadingMemosPage />
                </RoleRoute>
              </S>
            }
          />

          {/* Day 47-48 */}
          <Route
            path="/agents"
            element={
              <S>
                <RoleRoute
                  allowedRoles={["super_admin", "admin", "area_manager"]}
                >
                  <AgentsPage />
                </RoleRoute>
              </S>
            }
          />
          <Route
            path="/agents/:id"
            element={
              <S>
                <RoleRoute allowedRoles={["super_admin"]}>
                  <AgentDetailPage />
                </RoleRoute>
              </S>
            }
          />
          <Route
            path="/driver-devices"
            element={
              <S>
                <RoleRoute allowedRoles={["super_admin"]}>
                  <DriverDevicesPage />
                </RoleRoute>
              </S>
            }
          />
        </Route>

        {/* Fallback */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route
          path="/500"
          element={
            <S>
              <ServerErrorPage />
            </S>
          }
        />
        <Route
          path="/maintenance"
          element={
            <S>
              <MaintenancePage />
            </S>
          }
        />
        <Route
          path="*"
          element={
            <S>
              <NotFoundPage />
            </S>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

// Day 75 complete
// Day 85: wrapped restricted routes with RoleRoute guard per L1/L2/L3 access table
